import { z } from 'zod';

export const DeviceSchema = z.enum(['desktop', 'tablet', 'mobile']);
export type Device = z.infer<typeof DeviceSchema>;

export const RoleSchema = z.enum(['admin', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

export const InsightTypeSchema = z.enum([
  'rage_click',
  'dead_click',
  'quick_back',
  'error',
  'excessive_scroll',
]);
export type InsightType = z.infer<typeof InsightTypeSchema>;

export const SiteSettingsSchema = z.object({
  mask_inputs: z.boolean(),
  record_sessions: z.boolean(),
  sample_rate: z.number().min(0).max(1),
  retention_days: z.number().int().positive(),
});
export type SiteSettings = z.infer<typeof SiteSettingsSchema>;

export const SiteSchema = z.object({
  id: z.string(),
  name: z.string(),
  domain: z.string(),
  api_key: z.string(),
  settings: SiteSettingsSchema,
  stats: z.object({
    sessions_24h: z.number().int().nonnegative(),
    sessions_7d: z.number().int().nonnegative(),
    last_event_at: z.number().nullable(),
  }),
  created_at: z.number(),
  created_by: z.object({ id: z.string(), email: z.string().email() }),
});
export type Site = z.infer<typeof SiteSchema>;

export const SessionFlagsSchema = z.object({
  rage_click: z.boolean(),
  dead_click: z.boolean(),
  quick_back: z.boolean(),
  error: z.boolean(),
});
export type SessionFlags = z.infer<typeof SessionFlagsSchema>;

export const SessionSchema = z.object({
  id: z.string(),
  site_id: z.string(),
  visitor_id: z.string(),
  started_at: z.number(),
  ended_at: z.number().nullable(),
  duration_ms: z.number().nullable(),
  pages_viewed: z.number().int().nonnegative(),
  events_count: z.number().int().nonnegative(),
  entry_url: z.string(),
  exit_url: z.string().nullable(),
  device: DeviceSchema,
  browser: z.string(),
  os: z.string(),
  country: z.string(),
  flags: SessionFlagsSchema,
  has_replay: z.boolean(),
  replay_size: z.number().nullable(),
});
export type Session = z.infer<typeof SessionSchema>;

export const InsightSchema = z.object({
  id: z.string(),
  session_id: z.string(),
  type: InsightTypeSchema,
  url: z.string(),
  selector: z.string().nullable(),
  payload: z.record(z.unknown()),
  timestamp_ms: z.number(),
});
export type Insight = z.infer<typeof InsightSchema>;

export const MemberSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().nullable(),
  role: RoleSchema,
  created_at: z.number(),
  last_active: z.number().nullable(),
});
export type Member = z.infer<typeof MemberSchema>;

export const HeatmapPointSchema = z.object({
  x: z.number(),
  y: z.number(),
  density: z.number(),
});
export type HeatmapPoint = z.infer<typeof HeatmapPointSchema>;

export const HeatmapResponseSchema = z.object({
  points: z.array(HeatmapPointSchema),
  viewport: z.object({ w: z.number(), h: z.number() }),
  total_events: z.number(),
  generated_at: z.number(),
});
export type HeatmapResponse = z.infer<typeof HeatmapResponseSchema>;

export const OverviewResponseSchema = z.object({
  sessions: z.number(),
  unique_visitors: z.number(),
  avg_duration_ms: z.number(),
  pages_per_session: z.number(),
  bounce_rate: z.number(),
  by_device: z.object({
    desktop: z.number(),
    tablet: z.number(),
    mobile: z.number(),
  }),
  by_country: z.array(z.object({ code: z.string(), count: z.number() })),
  top_pages: z.array(z.object({ url: z.string(), views: z.number() })),
});
export type OverviewResponse = z.infer<typeof OverviewResponseSchema>;

export const RangeSchema = z.enum(['24h', '7d', '30d']);
export type Range = z.infer<typeof RangeSchema>;
